import { Input, INPUT_TYPES } from '@iterable/deprecated_react_components';
import { MessageMedium } from 'constants/messageMediums';
import { BASE, CAMPAIGN, TemplateSource, WORKFLOW } from 'constants/templateTypes';
import { selectIsSupportChatTestFeatureFlagOn } from 'core/userContext';
import { TemplatePicker } from 'modules/templatePicker';
import type { Locale } from 'modules/workflowNodeEditors/components/sendNodeEditors/BaseSendEditor/BaseSendEditor.types';
import React, { FC, SyntheticEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { BUTTON_VARIANTS, ChatButton } from 'ui/button';
import { Tab, TabGroup } from 'ui/tabs';
import { fonts, spacing } from 'ui/theme';
import { Takeover, TakeoverFooterButton } from 'ui/workflows/takeover';
import { pxToRem } from 'utils/convert';
import { useDebouncedState } from 'utils/hooks/useDebouncedState';
import noop from 'utils/noop';

const MainContentContainer = styled.section`
  font-family: ${fonts.sansSerif} !important;
  padding: 0 ${spacing.double};
  > :first-child {
    padding-bottom: ${spacing.plusHalf};
  }
`;

const ActionsContainer = styled.section`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const StyledInput = styled(Input)`
  min-width: ${pxToRem(200)};
  width: 30%;
  > input {
    font-family: ${fonts.sansSerif} !important;
  }
`;

const StyledTakeoverFooterButton = styled(TakeoverFooterButton)<{
  $supportChatTest: boolean;
}>`
  ${({ $supportChatTest }) => $supportChatTest && 'margin-left: auto;'}
`;

export interface TemplateData {
  id: number;
  contentId: number;
  name: string;
  contentPreview: string;
  locales: Locale[];
}

export interface AttachTemplateTakeoverProps {
  /** content is just a passed in object with strings
   *  representing all of the text on the page.
   *  this makes Justin Tulk happy because it's prep work
   *  for a possible i18n future.
   */
  content?: Record<string, string>;
  /** string representation of which type of templates to display */
  messageMedium: MessageMedium;
  /** Callback when the takeover is closed */
  onClose?: (e: SyntheticEvent) => void;
  /** Callback when the template is attached */
  onAttachTemplate?: (id?: number, config?: any) => void;
  /** Selected template id */
  templateId?: number;
  /** The placement id of the template -- only necessary if the template is embedded */
  placementId?: number;
}

// TODO: [PRO-3634] `displayContent` convention for localization. Turn text to translate into a localization en-US.json
const displayContent = {
  campaignLabel: 'Campaign content',
  inputPlaceholder: 'Search by template ID or name',
  takeoverTitle: 'Attach a template',
  templateLabel: 'Templates',
  title: 'Select Template',
  workflowLabel: 'Workflow content',
};

export const AttachTemplateTakeover: FC<React.PropsWithChildren<AttachTemplateTakeoverProps>> = ({
  content = displayContent,
  messageMedium = MessageMedium.Email,
  onClose = noop,
  onAttachTemplate = noop,
  templateId,
  placementId,
}): JSX.Element => {
  const [debouncedTemplateFilter, templateFilter, setTemplateFilter] = useDebouncedState('', 500);
  const [selectedTab, setSelectedTab] = useState<TemplateSource>(TemplateSource.Base);

  // this value is null when re-loading the takeover after attaching
  // a template; it is only updated when a new selection is made
  // in the current instantiation of the template picker
  // (i.e. *changing* your template selection)
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateData | null>(null);

  // code for ada/ zendesk support chat
  const isSupportChatTestEnabled = useSelector(selectIsSupportChatTestFeatureFlagOn);

  return (
    <Takeover
      data-test="attach-template-takeover"
      onClose={onClose}
      title={content.takeoverTitle}
      HeaderBarContent={isSupportChatTestEnabled && <ChatButton />}
      Body={
        <MainContentContainer>
          <ActionsContainer data-test="attach-template-takeover--tab-list-container">
            {/**
             * Only displaying base templates tab for beta and
             * hiding those for campaign and workflow templates, see
             * WF-1608 and WF-1604 for more context
             */}
            <TabGroup selectedTab={selectedTab} onTabChange={(id) => setSelectedTab(id)}>
              <Tab id={BASE}>{content.templateLabel}</Tab>
              <Tab id={CAMPAIGN}>{content.campaignLabel}</Tab>
              <Tab id={WORKFLOW}>{content.workflowLabel}</Tab>
            </TabGroup>
            <StyledInput
              value={templateFilter}
              type={INPUT_TYPES.TEXT}
              // @ts-ignore
              onChange={(_, { value }) => setTemplateFilter(value as string)}
              placeholder={content.inputPlaceholder}
              icon="search"
              data-test="attach-template-takeover--search-input"
            />
          </ActionsContainer>
          <TemplatePicker
            filter={debouncedTemplateFilter}
            forceRefresh
            messageMedium={messageMedium}
            // @ts-ignore
            onSelectTemplate={(template) => setSelectedTemplate(template)}
            templateId={templateId}
            selectedTab={selectedTab}
            placementId={placementId}
          />
        </MainContentContainer>
      }
      Footer={
        <StyledTakeoverFooterButton
          $supportChatTest={isSupportChatTestEnabled}
          variant={BUTTON_VARIANTS.PRIMARY}
          onClick={() => onAttachTemplate(selectedTemplate?.id, selectedTemplate)}
        >
          Use selected template
        </StyledTakeoverFooterButton>
      }
    />
  );
};

export default AttachTemplateTakeover;
